import React from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  FormControl,
  Input,
  Select,
  VStack,
  HStack,
  Box,
  IconButton,
  Text,
  useColorModeValue,
} from "@chakra-ui/react";
import { AddIcon, DeleteIcon } from "@chakra-ui/icons";

import GlobalEntity from "../GlobalEntity";
import {
  DateFilter,
  GrowthBenchmarkType,
} from "build-ts/relative_date";
import { AggregationType } from "build-ts/enums";
import { DateFilterCategory } from "utils/enum";
import { getBenchmarkLabel } from "utils/nameUtils";
import { useBatchContext } from "context/batch/BatchContext";
import { AnalysisTreeGenerationRules_NodeInheritanceRules } from "build-ts/analysis";
import { isDateFilterValid } from "utils/dateUtils";

interface DiagnosticModalProps {
  isOpen: boolean;
  onClose: () => void;
  dateFilter: DateFilter | undefined;
  dateFilterCategory: DateFilterCategory | undefined;
  setDateFilter: React.Dispatch<React.SetStateAction<DateFilter | undefined>>;
  setDateFilterCategory: React.Dispatch<React.SetStateAction<DateFilterCategory>>;
  id?: string; 
}

const DiagnosticModal: React.FC<DiagnosticModalProps> = ({ 
  isOpen, 
  onClose, 
  dateFilter, 
  setDateFilter,
  dateFilterCategory,
  setDateFilterCategory
}) => {
  const {
    changeBenchmarkAtDepth,
    setChangeBenchmarkAtDepth,
    diagnosticBenchmarks,
    setDiagnosticBenchmarks,
    selectedBenchmarkType,
    setSelectedBenchmarkType,
    numberOfPeriods,
    setNumberOfPeriods,
    aggregationType,
    setAggregationType
  } = useBatchContext();
  const borderColor = useColorModeValue("gray.200", "gray.600");

  /**
   * Add a new Growth Benchmark
   */
  const addBenchmark = () => {
    if (selectedBenchmarkType === GrowthBenchmarkType.LAST_N_PERIOD_AGGREGATION) {
      setDiagnosticBenchmarks((prev) => [
        ...prev,
        {
          growth_type: selectedBenchmarkType,
          number: numberOfPeriods,
          aggregation_type: aggregationType,
        },
      ]);
      // Reset inputs
      setSelectedBenchmarkType(undefined);
      setNumberOfPeriods(undefined);
      setAggregationType(undefined);
    } else {
      setDiagnosticBenchmarks((prev) => [
        ...prev,
        { growth_type: selectedBenchmarkType },
      ]);
      setSelectedBenchmarkType(undefined);
    }
  };

  /**
   * Remove a benchmark by index
   */
  const removeBenchmark = (index: number) => {
    setDiagnosticBenchmarks((prev) => prev.filter((_, i) => i !== index));
  };

  /**
   * Submit data and close modal
   */
  const handleSubmit = () => {
    if(!isDateFilterValid(dateFilter!)) return;
    const inheritance_rules: AnalysisTreeGenerationRules_NodeInheritanceRules = {
        child_node_depth: parseInt(changeBenchmarkAtDepth),
        new_benchmarks: diagnosticBenchmarks ?? [],
        new_date_filter: dateFilter!
    };
    console.log("inheritance_rules", inheritance_rules);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />

      <ModalContent>
        <ModalHeader>Diagnostic Settings</ModalHeader>
        <ModalCloseButton />

        <ModalBody>
          <VStack spacing={6} align="stretch">
            {/* Benchmark Depth */}
            <FormControl>
              <Text fontWeight="bold" mb={2}>
                Change filter at depth
              </Text>
              <Input
                value={changeBenchmarkAtDepth}
                onChange={(e) => setChangeBenchmarkAtDepth(e.target.value)}
                placeholder="Enter benchmark depth"
              />
            </FormControl>

            {/* Date Filter */}
            <FormControl>
            <Text fontWeight="bold" mb={2}>
              Date Filter
            </Text>
              <GlobalEntity
                setDateFilter={setDateFilter}
                dateFilter={dateFilter}
                setDateFilterCategory={setDateFilterCategory}
                dateFilterCategory={dateFilterCategory}
                id="diagnostic"
              />
            </FormControl>

            {/* Growth Benchmarks Section */}
            <Box>
              <Text fontWeight="bold" mb={2}>
                Growth Benchmarks
              </Text>

              {/* Benchmark Selector */}
              <VStack spacing={4} align="stretch">
                <HStack spacing={4} align="center" mb={2}>
                  {/* Dropdown to Select Growth Benchmark Type */}
                  <FormControl maxW="200px">
                    <Select
                      placeholder="Select growth benchmark"
                      value={selectedBenchmarkType ?? ""}
                      onChange={(e) => {
                        const selectedType = parseInt(e.target.value, 10) as GrowthBenchmarkType;
                        setSelectedBenchmarkType(selectedType);

                        // Reset dependent fields if changing type
                        if (selectedType !== GrowthBenchmarkType.LAST_N_PERIOD_AGGREGATION) {
                          setNumberOfPeriods(undefined);
                          setAggregationType(undefined);
                        }
                      }}
                    >
                      <option value={GrowthBenchmarkType.PRIOR_PERIOD}>Prior Period</option>
                      <option value={GrowthBenchmarkType.LAST_N_PERIOD_AGGREGATION}>
                        Last N Periods Aggregate
                      </option>
                    </Select>
                  </FormControl>

                  {/* Inputs for Last N Periods Aggregate */}
                  {selectedBenchmarkType === GrowthBenchmarkType.LAST_N_PERIOD_AGGREGATION && (
                    <>
                      <FormControl maxW="120px">
                        <Input
                          type="number"
                          size="sm"
                          placeholder="N"
                          value={numberOfPeriods ?? ""}
                          min={1}
                          onChange={(e) =>
                            setNumberOfPeriods(parseInt(e.target.value, 10) || undefined)
                          }
                        />
                      </FormControl>

                      <FormControl maxW="160px">
                        <Select
                          size="sm"
                          placeholder="Aggregation"
                          value={aggregationType ?? ""}
                          onChange={(e) =>
                            setAggregationType(
                              parseInt(e.target.value, 10) as AggregationType
                            )
                          }
                        >
                          <option value={AggregationType.NO_AGGREGATION}>
                            No Aggregation
                          </option>
                          <option value={AggregationType.SUM}>Sum</option>
                          <option value={AggregationType.AVERAGE}>Average</option>
                          <option value={AggregationType.COUNT}>Count</option>
                          <option value={AggregationType.UNIQUE_COUNT}>
                            Unique Count
                          </option>
                          <option value={AggregationType.MIN}>Min</option>
                          <option value={AggregationType.MAX}>Max</option>
                          <option value={AggregationType.GROWTH}>Growth</option>
                        </Select>
                      </FormControl>
                    </>
                  )}

                  {/* Add Button */}
                  <Button
                    borderRadius="full"
                    variant="solid"
                    bg="purple.700"
                    leftIcon={<AddIcon />}
                    size="xs"
                    iconSpacing={0}
                    onClick={addBenchmark}
                    isDisabled={
                      selectedBenchmarkType ===
                        GrowthBenchmarkType.LAST_N_PERIOD_AGGREGATION &&
                      (numberOfPeriods === undefined || aggregationType === undefined)
                    }
                  />
                </HStack>

              </VStack>

              {/* Selected Benchmarks */}
              {diagnosticBenchmarks.length === 0 ? (
                <Text color="gray.500" fontSize="sm" mt={4}>
                  No benchmarks added yet.
                </Text>
              ) : (
                <VStack spacing={2} align="stretch" mt={2}>
                  {diagnosticBenchmarks?.map((benchmark, index) => (
                    <HStack
                      key={index}
                      justify="space-between"
                      p={1}
                      borderWidth="1px"
                      borderRadius="md"
                      borderColor={borderColor}
                      boxShadow="sm"
                      _hover={{
                        boxShadow: "md",
                        bg: "gray.50",
                      }}
                    >
                      <VStack align="start" spacing={0}>
                        <Text fontSize="sm" fontWeight={600} mb={0}>
                          {getBenchmarkLabel(
                            benchmark?.growth_type!,
                            benchmark.number,
                            benchmark.aggregation_type
                          )}
                        </Text>
                      </VStack>
                      <IconButton
                        icon={<DeleteIcon />}
                        onClick={() => removeBenchmark(index)}
                        variant="outline"
                        colorScheme="red"
                        aria-label="Remove benchmark"
                        size="xs"
                        _hover={{
                          bg: "red.50",
                          borderColor: "red.400",
                        }}
                      />
                    </HStack>
                  ))}
                </VStack>
              )}
            </Box>
          </VStack>
        </ModalBody>

        {/* Modal Footer */}
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            Cancel
          </Button>
          <Button variant="solid" bg="purple.700" onClick={handleSubmit}>
            Apply
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default DiagnosticModal;
